import { ChangeDetectionStrategy, Component, computed, effect, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OperaCloudOcimPmsSetup, PmsType } from '../../../../../../types/pms';

@Component({
    selector: 'juyo-opera-cloud-ocim-connect',
    styleUrl: './ocim.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    template: `
        <button type="button" class="back-link" (click)="back.emit()">
            <span class="material-symbols-outlined">arrow_left_alt</span>
            Change connection type
        </button>

        <p class="section-label">OCIM CONFIGURATION</p>

        <div class="field-grid">
            <div class="field">
                <label class="field-label" for="ocim-enterprise-id"
                    >ENTERPRISE ID <span class="required">*</span></label
                >
                <input
                    id="ocim-enterprise-id"
                    type="text"
                    class="field-input"
                    placeholder="e.g. JUYOENT"
                    [ngModel]="enterpriseId()"
                    (ngModelChange)="enterpriseId.set($event)"
                />
                <p class="field-hint">Developer Portal &rarr; Environments &rarr; View Details</p>
            </div>

            <div class="field">
                <label class="field-label" for="ocim-chain-id">CHAIN ID <span class="required">*</span></label>
                <input
                    id="ocim-chain-id"
                    type="text"
                    class="field-input"
                    placeholder="e.g. JUYOCHAIN"
                    [ngModel]="chainId()"
                    (ngModelChange)="chainId.set($event)"
                />
                <p class="field-hint">Developer Portal &rarr; Environments &rarr; View Details</p>
            </div>

            <div class="field">
                <label class="field-label" for="ocim-property-id">PROPERTY ID <span class="required">*</span></label>
                <input
                    id="ocim-property-id"
                    type="text"
                    class="field-input"
                    placeholder="e.g. LON01"
                    [ngModel]="propertyId()"
                    (ngModelChange)="propertyId.set($event)"
                />
                <p class="field-hint">Developer Portal &rarr; Environments &rarr; View Details</p>
            </div>

            <div class="field">
                <label class="field-label" for="ocim-login-link"
                    >HOTEL LOGIN LINK <span class="required">*</span></label
                >
                <input
                    id="ocim-login-link"
                    type="text"
                    class="field-input"
                    placeholder="https://xxxx.oraclehospitality.eu-frankfurt-1.ocs."
                    [ngModel]="hotelLoginLink()"
                    (ngModelChange)="hotelLoginLink.set($event)"
                />
                <p class="field-hint">Paste the URL you use to log into Opera Cloud.</p>
            </div>
        </div>

        <div class="next-box">
            <span class="material-symbols-outlined next-icon">info</span>
            <div class="next-content">
                <p class="next-title">What happens next</p>
                <p class="next-body">
                    Once you submit, Juyo will send a partner connection request to your tenant. Approve it in
                    <strong>Developer Portal &gt; Environments &gt; Partner Connections</strong> to start data flow.
                    We'll email you when the request is sent.
                </p>
            </div>
        </div>

        <p class="guide-link-text">
            Need more detail? Read the
            <a class="guide-link" [href]="guideUrl" target="_blank" rel="noopener noreferrer">
                full Opera Cloud onboarding guide →
            </a>
        </p>
    `,
})
export class OperaCloudOcimConnectComponent {
    public back = output<void>();
    public valueChange = output<OperaCloudOcimPmsSetup | null>();

    protected readonly guideUrl =
        'https://intercom-help.eu/juyo-analytics/en/articles/618454-opera-cloud-onboarding-what-to-expect';

    protected enterpriseId = signal('');
    protected chainId = signal('');
    protected propertyId = signal('');
    protected hotelLoginLink = signal('');

    private setup = computed<OperaCloudOcimPmsSetup | null>(() => {
        const enterpriseId = this.enterpriseId().trim();
        const chainId = this.chainId().trim();
        const propertyId = this.propertyId().trim();
        const hotelLoginLink = this.hotelLoginLink().trim();

        if (!enterpriseId || !chainId || !propertyId || !hotelLoginLink) {
            return null;
        }

        return {
            type: PmsType.OperaCloud,
            path: 'OCIM',
            enterpriseId,
            chainId,
            propertyId,
            hotelLoginLink,
        };
    });

    constructor() {
        effect(() => this.valueChange.emit(this.setup()));
    }
}
